import React, { useState } from 'react';
import { X, ChevronRight, Calendar, Clock, Truck, Package, Tag, Shield, AlertCircle, Edit, Plus, FileText } from 'lucide-react';
import { formatDate, formatTime, isTruckScheduledForDate } from '../utils/dateUtils';
import { countRefList, formatRefListCompact, formatRefListFull } from '../utils/refListUtils';
import { useTimezone } from '../../../../../hooks/useTimezone';
import BusinessUnitName from '../BusinessUnitName';
import TruckNotesThread from '../TruckNotesThread';

/**
 * Component that displays truck details for a selected date
 */
const DetailPanel = ({ 
  selectedDate, 
  truckData, 
  onClose, 
  setShowScheduleForm, 
  setPreSelectedDate,
  openTruckEditor,
  mode = 'inbound'
}) => {
  // State to track which truck's details are expanded
  const [selectedTruck, setSelectedTruck] = useState(null);
  
  // Get user's timezone for proper date handling
  const userTimezone = useTimezone();
  
  // Select theme colors based on mode
  const themeColor = mode === 'inbound' ? 'blue' : 'green';
  const ModeIcon = mode === 'inbound' ? Truck : Package;
  
  // Filter trucks for selected date with proper timezone handling
  const trucksForDate = selectedDate
    ? truckData.filter(truck => isTruckScheduledForDate(truck, selectedDate, userTimezone))
    : [];
  
  // Group trucks by status
  const scheduledTrucks = trucksForDate.filter(t =>
    mode === 'inbound'
      ? (t.status === 'Scheduled')
      : (t.status === 'Scheduled')
  );

  const inProgressTrucks = trucksForDate.filter(t =>
    mode === 'inbound'
      ? (t.status === 'Arrived' || t.status === 'Received' || t.status === 'Putaway')
      : (t.status === 'Staged' || t.status === 'Loaded')
  );

  const completedTrucks = trucksForDate.filter(t =>
    mode === 'inbound'
      ? (t.status === 'Completed')
      : (t.status === 'Shipped' || t.status === 'Completed')
  );
  
  // Handle scheduling a new truck
  const handleScheduleNew = () => {
    if (selectedDate) {
      const dateStr = formatDate(selectedDate, 'yyyy-MM-dd');
      setPreSelectedDate(dateStr);
      setShowScheduleForm(true);
    }
  };
  
  // Render a truck item in the list
  const renderTruckItem = (truck) => {
    const isSelected = selectedTruck && selectedTruck.id === truck.id;
    
    // Get status classes
    let statusClasses = 'px-2 py-0.5 text-xs rounded-full';
    let statusColor = 'gray';

    if (mode === 'inbound') {
      if (truck.status === 'Scheduled') {
        statusColor = 'yellow';
      } else if (truck.status === 'Arrived' || truck.status === 'Received' || truck.status === 'Putaway') {
        statusColor = 'blue';
      } else if (truck.status === 'Completed') {
        statusColor = 'green';
      } else if (truck.status === 'Cancelled') {
        statusColor = 'red';
      }
    } else {
      if (truck.status === 'Scheduled') {
        statusColor = 'yellow';
      } else if (truck.status === 'Staged' || truck.status === 'Loaded') {
        statusColor = 'blue';
      } else if (truck.status === 'Shipped' || truck.status === 'Completed') {
        statusColor = 'green';
      } else if (truck.status === 'Cancelled') {
        statusColor = 'red';
      }
    }
    
    statusClasses += ` bg-${statusColor}-500/20 text-${statusColor}-300 border border-${statusColor}-500/30`;
    
    return (
      <div 
        key={truck.id}
        className={`p-3 border ${isSelected ? `border-${themeColor}-500 bg-${themeColor}-900/20` : 'border-slate-700 hover:bg-slate-750'} rounded-md mb-2 cursor-pointer transition-colors`}
        onClick={() => setSelectedTruck(isSelected ? null : truck)}
      >
        <div className="flex justify-between items-start">
          <div>
            <div className="font-medium text-slate-200">{truck.carrier}</div>
            <div className="text-sm text-slate-400"><BusinessUnitName id={truck.business_unit} /></div>
            {truck.conf_number && (
              <div className="text-xs text-slate-400 mt-0.5">
                Conf #: <span className="text-slate-200 font-medium">{truck.conf_number}</span>
              </div>
            )}
          </div>
          <div className={statusClasses}>
            {truck.status}
          </div>
        </div>
        
        <div className="mt-2 flex items-center text-xs text-slate-400">
          <Clock className="w-3 h-3 mr-1" />
          <span>{formatTime(truck.scheduled_at, 'h:mm a', userTimezone)}</span>
          <span className="mx-2">•</span>
          <div className={`w-2 h-2 rounded-full ${truck.secure ? 'bg-red-500' : 'bg-slate-500'} mr-1`}></div>
          <span>{truck.secure ? 'Secure' : 'Standard'}</span>
          {truck.dock_door && (
            <>
              <span className="mx-2">•</span>
              <span>Dock {truck.dock_door}</span>
            </>
          )}
          {truck.load_material && (
            <>
              <span className="mx-2">•</span>
              <span>{truck.load_material}</span>
            </>
          )}
          {/* Collapsed to "first +N" for multi-order trucks — this is a dense summary line.
              The full list is spelled out in the expanded section below. */}
          {truck.po && (
            <>
              <span className="mx-2">•</span>
              <span className="flex items-center whitespace-nowrap" title={formatRefListFull(truck.po)}>
                <FileText className="w-3 h-3 mr-1" />
                PO: {formatRefListCompact(truck.po)}
              </span>
            </>
          )}
          {truck.order_number && (
            <>
              <span className="mx-2">•</span>
              <span className="flex items-center whitespace-nowrap" title={formatRefListFull(truck.order_number)}>
                <FileText className="w-3 h-3 mr-1" />
                Order: {formatRefListCompact(truck.order_number)}
              </span>
            </>
          )}
        </div>
        
        {isSelected && (
          <div className="mt-3 pt-3 border-t border-slate-700">
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-wider">Details</h4>
              {openTruckEditor && (
                <button 
                  className={`px-2 py-1 text-xs rounded-md ${mode === 'inbound' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'} text-white flex items-center`}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent click from propagating to parent
                    openTruckEditor(truck);
                  }}
                >
                  <Edit className="w-3 h-3 mr-1" />
                  Edit
                </button>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-2 text-sm">
              {truck.conf_number && (
                <div>
                  <div className="text-slate-400">Confirmation Number</div>
                  <div className="text-slate-200 font-medium">{truck.conf_number}</div>
                </div>
              )}
              <div>
                <div className="text-slate-400">Load Type</div>
                <div className="text-slate-200">{truck.load_type}</div>
              </div>
              <div>
                <div className="text-slate-400">Size</div>
                <div className="text-slate-200">{truck.size} pallets</div>
              </div>
              <div>
                <div className="text-slate-400">Load Material</div>
                <div className="text-slate-200">{truck.load_material || "—"}</div>
              </div>
              
              {/* Inbound-specific fields */}
              {mode === 'inbound' && truck.po && (
                <div>
                  <div className="text-slate-400">PO Number</div>
                  <div className="text-slate-200">{truck.po}</div>
                </div>
              )}
              
              {/* Outbound-specific fields */}
              {mode === 'outbound' && (
                <>
                  {truck.order_id && (
                    <div>
                      <div className="text-slate-400">Order ID</div>
                      <div className="text-slate-200">{truck.order_id}</div>
                    </div>
                  )}
                  {/* Full lists here — an operator reading the detail panel needs every PO and
                      order on a multi-order truck, so wrap rather than collapse. */}
                  {truck.po && (
                    <div>
                      <div className="text-slate-400">
                        {countRefList(truck.po) > 1 ? `PO Numbers (${countRefList(truck.po)})` : 'PO Number'}
                      </div>
                      <div className="text-slate-200 break-words">{formatRefListFull(truck.po)}</div>
                    </div>
                  )}
                  {truck.order_number && (
                    <div>
                      <div className="text-slate-400">
                        {countRefList(truck.order_number) > 1
                          ? `Order Numbers (${countRefList(truck.order_number)})`
                          : 'Order Number'}
                      </div>
                      <div className="text-slate-200 break-words">{formatRefListFull(truck.order_number)}</div>
                    </div>
                  )}
                  {truck.customer && (
                    <div>
                      <div className="text-slate-400">Customer</div>
                      <div className="text-slate-200">{truck.customer}</div>
                    </div>
                  )}
                  {truck.destination && (
                    <div>
                      <div className="text-slate-400">Destination</div>
                      <div className="text-slate-200">{truck.destination}</div>
                    </div>
                  )}
                  {truck.priority && (
                    <div>
                      <div className="text-slate-400">Priority</div>
                      <div className="text-slate-200">{truck.priority}</div>
                    </div>
                  )}
                </>
              )}
            </div>
            
            <div className="mt-3">
              <div className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">Notes</div>
              <TruckNotesThread
                truckType={mode}
                truckId={truck.id}
                mode="edit"
                theme="dark"
                emptyMessage="No notes yet."
              />
            </div>
            
            {/* Timeline / Status events */}
            <div className="mt-3">
              <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Timeline</h4>
              
              <div className="relative pl-5 pb-2 border-l border-slate-700 text-sm">
                <div className="mb-3">
                  <div className="absolute left-0 w-2.5 h-2.5 rounded-full bg-green-500 -translate-x-1/2 mt-0.5"></div>
                  <div className="text-slate-300">Scheduled</div>
                  <div className="text-slate-400">{formatDate(truck.scheduled_at, 'MMM dd, yyyy h:mm a', userTimezone)}</div>
                </div>
                
                {/* Use arrived_at field (fixed from arrival_datetime) */}
                {truck.arrived_at && (
                  <div className="mb-3">
                    <div className="absolute left-0 w-2.5 h-2.5 rounded-full bg-blue-500 -translate-x-1/2 mt-0.5"></div>
                    <div className="text-slate-300">Arrived</div>
                    <div className="text-slate-400">{formatDate(truck.arrived_at, 'MMM dd, yyyy h:mm a', userTimezone)}</div>
                  </div>
                )}
                
                {mode === 'inbound' && truck.received_at && (
                  <div className="mb-3">
                    <div className="absolute left-0 w-2.5 h-2.5 rounded-full bg-blue-500 -translate-x-1/2 mt-0.5"></div>
                    <div className="text-slate-300">Started Receiving</div>
                    <div className="text-slate-400">{formatDate(truck.received_at, 'MMM dd, yyyy h:mm a', userTimezone)}</div>
                  </div>
                )}
                
                {mode === 'inbound' && truck.putaway_at && (
                  <div className="mb-3">
                    <div className="absolute left-0 w-2.5 h-2.5 rounded-full bg-blue-500 -translate-x-1/2 mt-0.5"></div>
                    <div className="text-slate-300">Putaway</div>
                    <div className="text-slate-400">{formatDate(truck.putaway_at, 'MMM dd, yyyy h:mm a', userTimezone)}</div>
                  </div>
                )}
                
                {mode === 'outbound' && truck.staged_at && (
                  <div className="mb-3">
                    <div className="absolute left-0 w-2.5 h-2.5 rounded-full bg-blue-500 -translate-x-1/2 mt-0.5"></div>
                    <div className="text-slate-300">Started Staging</div>
                    <div className="text-slate-400">{formatDate(truck.staged_at, 'MMM dd, yyyy h:mm a', userTimezone)}</div>
                  </div>
                )}
                
                {truck.completed_at && (
                  <div>
                    <div className="absolute left-0 w-2.5 h-2.5 rounded-full bg-green-500 -translate-x-1/2 mt-0.5"></div>
                    <div className="text-slate-300">Completed</div>
                    <div className="text-slate-400">{formatDate(truck.completed_at, 'MMM dd, yyyy h:mm a', userTimezone)}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {!isSelected && (
          <div className="mt-1 text-xs text-slate-500 flex items-center">
            <span>View details</span>
            <ChevronRight className="w-3 h-3 ml-1" />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`bg-slate-800 border-l border-slate-700 p-4 w-full lg:w-80 h-full flex flex-col relative overflow-auto`}>
      <div className="flex justify-between items-center mb-4 sticky top-0 bg-slate-800 z-10 pb-2">
        <h3 className="text-lg font-semibold text-slate-200 flex items-center">
          <Calendar className={`w-5 h-5 mr-2 text-${themeColor}-400`} />
          {formatDate(selectedDate, 'MMMM d, yyyy', userTimezone)}
        </h3>
        <button 
          className="text-slate-400 hover:text-slate-200 p-1 rounded-full hover:bg-slate-700"
          onClick={onClose}
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      
      <div className="mb-4 flex">
        <button
          className={`flex-1 px-3 py-2 bg-${themeColor}-600 hover:bg-${themeColor}-700 text-white rounded-md flex items-center justify-center`}
          onClick={handleScheduleNew}
        >
          <Plus className="w-4 h-4 mr-2" />
          <span>Schedule New {mode === 'inbound' ? 'Inbound' : 'Outbound'}</span>
        </button>
      </div>
      
      {trucksForDate.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center p-4">
          <Calendar className="w-12 h-12 text-slate-500 mb-2" />
          <h3 className="text-lg font-semibold text-slate-300">No Trucks Scheduled</h3>
          <p className="text-slate-400 mb-4">No trucks are scheduled for this day.</p>
          <button
            className={`px-3 py-2 bg-${themeColor}-600 hover:bg-${themeColor}-700 text-white rounded-md flex items-center`}
            onClick={handleScheduleNew}
          >
            <Plus className="w-4 h-4 mr-2" />
            <span>Schedule Truck</span>
          </button>
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          {/* Scheduled trucks */}
          {scheduledTrucks.length > 0 && (
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2 flex items-center">
                <div className="w-2 h-2 rounded-full bg-yellow-500 mr-2"></div>
                Scheduled ({scheduledTrucks.length})
              </h4>
              <div>
                {scheduledTrucks.map(renderTruckItem)}
              </div>
            </div>
          )}
          
          {/* In progress trucks */}
          {inProgressTrucks.length > 0 && (
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2 flex items-center">
                <div className={`w-2 h-2 rounded-full bg-${themeColor}-500 mr-2`}></div>
                In Progress ({inProgressTrucks.length})
              </h4>
              <div>
                {inProgressTrucks.map(renderTruckItem)}
              </div>
            </div>
          )}
          
          {/* Completed trucks */}
          {completedTrucks.length > 0 && (
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2 flex items-center">
                <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                Completed ({completedTrucks.length})
              </h4>
              <div>
                {completedTrucks.map(renderTruckItem)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DetailPanel;